<template>
  <div class="qa-cards">
    <div class="qa-search" ref="searchRoot">
      <div class="qa-search__field">
        <i class="bi bi-search qa-search__icon" aria-hidden="true"></i>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="search"
          class="qa-search__input"
          placeholder="Search menus, pages, reports..."
          autocomplete="off"
          aria-label="Global quick access search"
          aria-autocomplete="list"
          aria-controls="qa-search-suggestions"
          :aria-expanded="showSuggestions ? 'true' : 'false'"
          @focus="onSearchFocus"
          @keydown="onSearchKeydown"
          @input="onSearchInput"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="qa-search__clear"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>
        <kbd v-else class="qa-search__hint">/</kbd>
      </div>

      <ul
        v-if="showSuggestions"
        id="qa-search-suggestions"
        class="qa-search__suggestions"
        role="listbox"
      >
        <li
          v-for="(item, index) in searchSuggestions"
          :key="`${item.section}-${item.label}-${item.path}`"
          class="qa-search__suggestion"
          :class="{ 'qa-search__suggestion--active': index === activeSuggestionIndex }"
          role="option"
          :aria-selected="index === activeSuggestionIndex"
          @mousedown.prevent="selectSuggestion(item)"
          @mouseenter="activeSuggestionIndex = index"
        >
          <span class="qa-search__suggestion-circle" :class="`qa-search__suggestion-circle--${item.tone}`">
            <i :class="item.icon" aria-hidden="true"></i>
          </span>
          <span class="qa-search__suggestion-body">
            <span class="qa-search__suggestion-label" v-html="highlightMatch(item.label)"></span>
            <span class="qa-search__suggestion-section">{{ item.section }}</span>
          </span>
          <i class="bi bi-arrow-return-left qa-search__suggestion-enter" aria-hidden="true"></i>
        </li>
        <li v-if="!searchSuggestions.length" class="qa-search__empty">
          No matches for “{{ searchQuery.trim() }}”
        </li>
      </ul>
    </div>

    <div class="qa-cards__sections">
      <section
        v-if="recentlyUsedItems.length && !normalizedQuery"
        class="qa-cards__section qa-cards__section--recent qa-cards__section--full"
      >
        <header class="qa-cards__section-header">
          <span class="qa-cards__section-icon" aria-hidden="true">
            <i class="bi bi-clock-history"></i>
          </span>
          <div class="qa-cards__section-heading">
            <h3 class="qa-cards__section-title">Recently Used</h3>
            <p class="qa-cards__section-count">{{ recentlyUsedItems.length }} shortcuts</p>
          </div>
          <button
            type="button"
            class="qa-cards__clear-recent"
            @click="clearRecentUsage"
          >
            Clear
          </button>
        </header>
        <div class="qa-cards__grid">
          <router-link
            v-for="item in recentlyUsedItems"
            :key="`recent-${item.path}`"
            :to="toPath(item.path)"
            class="qa-card"
            @click="recordUsage(item)"
          >
            <span class="qa-card__circle" :class="`qa-card__circle--${item.tone}`" aria-hidden="true">
              <span class="qa-card__shine"></span>
              <span class="qa-card__icon-wrap" :class="`qa-card__icon-wrap--${item.tone}`">
                <i :class="item.icon"></i>
                <i
                  v-if="item.badge"
                  :class="['qa-card__badge', item.badge]"
                ></i>
              </span>
            </span>
            <span class="qa-card__label">{{ item.label }}</span>
          </router-link>
        </div>
      </section>

      <template v-for="item in layoutItems" :key="item.id">
        <div v-if="item.type === 'row'" :class="item.className">
          <section
            v-for="section in item.sections"
            :key="section.title"
            class="qa-cards__section"
            :class="`qa-cards__section--${section.key}`"
          >
            <header class="qa-cards__section-header">
              <span class="qa-cards__section-icon" aria-hidden="true">
                <i :class="section.icon"></i>
              </span>
              <div class="qa-cards__section-heading">
                <h3 class="qa-cards__section-title">{{ section.title }}</h3>
                <p class="qa-cards__section-count">{{ section.items.length }} shortcuts</p>
              </div>
            </header>
            <div class="qa-cards__grid">
              <router-link
                v-for="link in section.items"
                :key="`${section.title}-${link.label}-${link.path}`"
                :to="toPath(link.path)"
                class="qa-card"
                @click="recordUsage({ ...link, section: section.title })"
              >
                <span class="qa-card__circle" :class="`qa-card__circle--${link.tone}`" aria-hidden="true">
                  <span class="qa-card__shine"></span>
                  <span class="qa-card__icon-wrap" :class="`qa-card__icon-wrap--${link.tone}`">
                    <i :class="link.icon"></i>
                    <i
                      v-if="link.badge"
                      :class="['qa-card__badge', link.badge]"
                    ></i>
                  </span>
                </span>
                <span class="qa-card__label">{{ link.label }}</span>
              </router-link>
            </div>
          </section>
        </div>
        <section
          v-else
          class="qa-cards__section"
          :class="`qa-cards__section--${item.section.key}`"
        >
          <header class="qa-cards__section-header">
            <span class="qa-cards__section-icon" aria-hidden="true">
              <i :class="item.section.icon"></i>
            </span>
            <div class="qa-cards__section-heading">
              <h3 class="qa-cards__section-title">{{ item.section.title }}</h3>
              <p class="qa-cards__section-count">{{ item.section.items.length }} shortcuts</p>
            </div>
          </header>
          <div class="qa-cards__grid">
            <router-link
              v-for="link in item.section.items"
              :key="`${item.section.title}-${link.label}-${link.path}`"
              :to="toPath(link.path)"
              class="qa-card"
              @click="recordUsage({ ...link, section: item.section.title })"
            >
              <span class="qa-card__circle" :class="`qa-card__circle--${link.tone}`" aria-hidden="true">
                <span class="qa-card__shine"></span>
                <span class="qa-card__icon-wrap" :class="`qa-card__icon-wrap--${link.tone}`">
                  <i :class="link.icon"></i>
                  <i
                    v-if="link.badge"
                    :class="['qa-card__badge', link.badge]"
                  ></i>
                </span>
              </span>
              <span class="qa-card__label">{{ link.label }}</span>
            </router-link>
          </div>
        </section>
      </template>
    </div>

    <p v-if="searchQuery.trim() && !visibleSections.length" class="qa-cards__no-results">
      No shortcuts match your search.
    </p>
  </div>
</template>

<script>
import './styles/QuickLinksWidget.css';

function card(label, icon, path, tone, badge) {
  return { label, icon, path, tone, badge };
}

export default {
  name: 'QuickLinksWidget',
  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      searchQuery: '',
      searchFocused: false,
      activeSuggestionIndex: 0,
      usageMap: {},
    };
  },
  computed: {
    usageStorageKey() {
      return `qa_frequent_${this.companyId}`;
    },
    company() {
      const fromStore = this.$store?.state?.currentCompany;
      if (fromStore && String(fromStore.id) === String(this.companyId)) {
        return fromStore;
      }
      return fromStore || null;
    },
    isConsultantIndustry() {
      const industryId = this.company?.industryId || this.company?.cp_industry_type;
      return Number(industryId) === 18;
    },
    billTracking() {
      return this.company && typeof this.company.billTracking !== 'undefined'
        ? !!this.company.billTracking
        : false;
    },
    allSearchableItems() {
      return this.cardSections.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          section: section.title,
          sectionKey: section.key,
        })),
      );
    },
    normalizedQuery() {
      return this.searchQuery.trim().toLowerCase();
    },
    searchSuggestions() {
      const q = this.normalizedQuery;
      if (!q) return [];

      const scored = this.allSearchableItems
        .map((item) => {
          const label = item.label.toLowerCase();
          const section = item.section.toLowerCase();
          let score = 0;
          if (label === q) score = 100;
          else if (label.startsWith(q)) score = 80;
          else if (label.includes(q)) score = 60;
          else if (section.includes(q)) score = 30;
          else if (`${section} ${label}`.includes(q)) score = 40;
          return { item, score };
        })
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label));

      return scored.slice(0, 10).map((entry) => entry.item);
    },
    showSuggestions() {
      return this.searchFocused && this.normalizedQuery.length > 0;
    },
    recentlyUsedItems() {
      const catalogue = new Map(
        this.allSearchableItems.map((item) => [item.path, item]),
      );

      return Object.values(this.usageMap)
        .map((entry) => {
          const live = catalogue.get(entry.path);
          if (!live && !entry.label) return null;
          return {
            label: live?.label || entry.label,
            icon: live?.icon || entry.icon || 'bi bi-link-45deg',
            path: entry.path,
            tone: live?.tone || entry.tone || 'slate',
            badge: live?.badge || entry.badge || '',
            section: live?.section || entry.section || '',
            count: entry.count || 1,
            lastUsed: entry.lastUsed || 0,
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.lastUsed - a.lastUsed)
        .slice(0, 12);
    },
    visibleSections() {
      const q = this.normalizedQuery;
      if (!q) return this.cardSections;

      return this.cardSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            const haystack = `${section.title} ${item.label}`.toLowerCase();
            return haystack.includes(q);
          }),
        }))
        .filter((section) => section.items.length > 0);
    },
    layoutItems() {
      const sections = this.visibleSections;
      const pick = (keys) => keys
        .map((key) => sections.find((section) => section.key === key))
        .filter(Boolean);
      const reserved = new Set([
        'consultant',
        'customer',
        'vendor',
        'inventory',
        'banking',
        'settings',
        'company',
        'reports',
      ]);

      const items = [];
      sections
        .filter((section) => !reserved.has(section.key))
        .forEach((section) => {
          items.push({
            id: `section-${section.key}`,
            type: 'section',
            section,
          });
        });

      const pushEqualHeightRow = (id, keys) => {
        const rowSections = pick(keys);
        if (!rowSections.length) return;
        const cols = rowSections.length >= 3 ? 3 : Math.max(rowSections.length, 2);
        items.push({
          id,
          type: 'row',
          className: `qa-cards__row qa-cards__row--${cols}`,
          sections: rowSections,
        });
      };

      pushEqualHeightRow('row-consultant-customer-vendor', [
        'consultant',
        'customer',
        'vendor',
      ]);
      pushEqualHeightRow('row-inventory-banking-settings', [
        'inventory',
        'banking',
        'settings',
      ]);
      pushEqualHeightRow('row-company-reports', ['company', 'reports']);

      return items;
    },
    cardSections() {
      const sections = [];

      if (this.isConsultantIndustry) {
        sections.push({
          key: 'consultant',
          title: 'Consultant',
          icon: 'bi bi-person',
          items: [
            card('Consultant Center', 'bi bi-house-door-fill', 'consultant/center', 'blue', 'bi bi-person-fill'),
            card('New Consultant', 'bi bi-person-fill', 'consultant/add-customer-consultant', 'green', 'bi bi-plus-lg'),
            card('Consultants', 'bi bi-people-fill', 'consultant/list', 'blue', 'bi bi-list-ul'),
            card('Received Payments', 'bi bi-cash-coin', 'consultant/receive-payment', 'teal', 'bi bi-currency-dollar'),
            card('Consultant Time Sheet', 'bi bi-clock-history', 'consultant/timesheet', 'indigo', 'bi bi-calendar3'),
            card('Timesheet Summary', 'bi bi-bar-chart-fill', 'consultant/timesheet-summary', 'violet', 'bi bi-graph-up'),
            card('Consultant Invoice', 'bi bi-receipt', 'consultant/invoice', 'indigo', 'bi bi-file-earmark-text'),
            card('Contract Details', 'bi bi-file-earmark-text-fill', 'consultant/contract-details', 'sky', 'bi bi-file-earmark-text'),
            card('Contract Tracking', 'bi bi-bullseye', 'consultant/contract-tracking', 'orange', 'bi bi-geo-alt'),
            card('Benefit Types', 'bi bi-tags-fill', 'consultant/benefits/types', 'red', 'bi bi-heart-pulse'),
            card('Benefit Plans', 'bi bi-clipboard2-heart-fill', 'consultant/benefits/plans', 'violet', 'bi bi-heart-pulse'),
            card('Payroll History', 'bi bi-clock-history', 'consultant/payroll/history', 'slate', 'bi bi-wallet2'),
            card('Record Payroll', 'bi bi-plus-circle-fill', 'consultant/payroll/record', 'green', 'bi bi-cash'),
            card('Staged Payroll', 'bi bi-bookmark-star-fill', 'consultant/payroll/staging', 'amber', 'bi bi-list-check'),
          ],
        });
      }

      sections.push({
        key: 'customer',
        title: 'Customer',
        icon: 'bi bi-people',
        items: [
          card('Customer Center', 'bi bi-house-door-fill', 'customers/center', 'blue', 'bi bi-people-fill'),
          card('New Customer', 'bi bi-person-fill', 'customers/new', 'green', 'bi bi-plus-lg'),
          card('New Sales Item', 'bi bi-box-seam-fill', 'customers/new-service-item', 'amber', 'bi bi-plus-lg'),
          card('Customer Invoice', 'bi bi-file-earmark-text-fill', 'customers/new-invoice', 'indigo', 'bi bi-receipt'),
          card('Create Invoice', 'bi bi-file-earmark-plus-fill', 'customers/create-invoice', 'violet', 'bi bi-plus-lg'),
          card('New Cash Sale', 'bi bi-cash-stack', 'customers/new-cash-sale', 'teal', 'bi bi-currency-dollar'),
          card('New Credit Memo', 'bi bi-receipt-cutoff', 'customers/new-credit-memo', 'orange', 'bi bi-arrow-return-left'),
          card('Customer Prepayment', 'bi bi-wallet-fill', 'customers/prepayment', 'sky', 'bi bi-cash'),
          card('Receive Payment', 'bi bi-person-fill', 'customers/receive-payment', 'green', 'bi bi-cash-coin'),
          card('Customer Refund', 'bi bi-person-fill', 'customers/refund', 'red', 'bi bi-arrow-counterclockwise'),
          card('Customers', 'bi bi-people-fill', 'customers/list', 'blue', 'bi bi-list-ul'),
          card('Invoices', 'bi bi-receipt', 'customers/invoices', 'indigo', 'bi bi-clock-history'),
          card('Customers Items', 'bi bi-box-seam', 'customers/items', 'violet', 'bi bi-grid'),
        ],
      });

      const vendorItems = [
        card('Vendor Center', 'bi bi-house-door-fill', 'vendors/center', 'blue', 'bi bi-shop'),
        card('New Vendor', 'bi bi-shop', 'vendors/new-vendor', 'green', 'bi bi-plus-lg'),
        card('New Purchase Item', 'bi bi-box-seam-fill', 'vendors/new-service-item', 'amber', 'bi bi-box-arrow-in-down'),
        card('Cash Purchase', 'bi bi-cash-stack', 'vendors/cash-purchase', 'teal', 'bi bi-currency-dollar'),
        card('Vendor Credit', 'bi bi-credit-card-fill', 'vendors/vendor-credit', 'violet', 'bi bi-plus-lg'),
        card('Write Check', 'bi bi-pencil-square', 'vendors/write-check', 'sky', 'bi bi-check2'),
        card('Print Check', 'bi bi-printer-fill', 'vendors/print-check', 'slate', 'bi bi-printer'),
        card('Vendor Bill', 'bi bi-file-earmark-text-fill', 'vendors/enter-bill', 'indigo', 'bi bi-receipt'),
      ];
      if (this.billTracking) {
        vendorItems.push(
          card('Create Bill', 'bi bi-file-earmark-plus-fill', 'vendors/create-bill', 'violet', 'bi bi-plus-lg'),
          card('Drafts & Captured', 'bi bi-file-earmark-diff-fill', 'vendors/draft-bills', 'indigo', 'bi bi-inbox'),
          card('Invites & Submitted', 'bi bi-box-arrow-in-right', 'vendors/portal-hub', 'teal', 'bi bi-send-check'),
          card('Bill Mail Setup', 'bi bi-envelope-check-fill', 'vendors/mail-sources', 'sky', 'bi bi-gear'),
          card('Pay Bill', 'bi bi-person-fill', 'vendors/pay-bill', 'teal', 'bi bi-cash'),
        );
      }
      vendorItems.push(
        card('Bills And Expenses', 'bi bi-receipt', 'vendors/bills', 'orange', 'bi bi-list-check'),
        card('Vendors', 'bi bi-shop-window', 'vendors/list', 'blue', 'bi bi-list-ul'),
        card('Vendors Items', 'bi bi-box-seam', 'vendors/items', 'violet', 'bi bi-grid'),
        card('Vendor Payments', 'bi bi-wallet2', 'vendors/payments', 'teal', 'bi bi-cash-coin'),
      );
      sections.push({
        key: 'vendor',
        title: 'Vendor',
        icon: 'bi bi-shop',
        items: vendorItems,
      });

      sections.push({
        key: 'inventory',
        title: 'Inventory',
        icon: 'bi bi-box-seam',
        items: [
          card('Inventory Centre', 'bi bi-house-door-fill', 'inventory/center', 'blue', 'bi bi-box-seam'),
          card('Build Assembly', 'bi bi-tools', 'inventory/build-assembly', 'amber', 'bi bi-gear-fill'),
          card('Stock Adjustment', 'bi bi-arrow-left-right', 'inventory/stock-adjustment/new', 'orange', 'bi bi-plus-lg'),
          card('New Inventory Item', 'bi bi-box-fill', 'inventory/new-item', 'green', 'bi bi-plus-lg'),
          card('New Inventory Assembly', 'bi bi-boxes', 'inventory/new-assembly', 'violet', 'bi bi-plus-lg'),
          card('Inventory Items', 'bi bi-box-seam', 'inventory/items', 'indigo', 'bi bi-list-ul'),
          card('Stock Adjustments', 'bi bi-arrow-left-right', 'inventory/stock-adjustment', 'sky', 'bi bi-clock-history'),
        ],
      });

      sections.push({
        key: 'banking',
        title: 'Banking',
        icon: 'bi bi-bank',
        items: [
          card('New Bank Account', 'bi bi-bank', 'banking/new-account', 'blue', 'bi bi-plus-lg'),
          card('Write Check', 'bi bi-pencil-square', 'vendors/write-check', 'sky', 'bi bi-check2'),
          card('Transfer Fund', 'bi bi-arrow-left-right', 'banking/transfer-fund', 'teal', 'bi bi-currency-exchange'),
          card('Pay Credit Card', 'bi bi-credit-card-fill', 'banking/pay-credit-card', 'indigo', 'bi bi-cash'),
          card('Add Credit Card', 'bi bi-credit-card-2-front-fill', 'banking/credit-cards/add', 'violet', 'bi bi-plus-lg'),
          card('Credit Cards Details', 'bi bi-card-list', 'banking/credit-cards/details', 'slate', 'bi bi-list-ul'),
          card('Add Loan', 'bi bi-cash-stack', 'banking/loans/add', 'green', 'bi bi-plus-lg'),
          card('Loans Details', 'bi bi-card-list', 'banking/loans/details', 'blue', 'bi bi-list-ul'),
          card('Record Loan Payment', 'bi bi-cash-coin', 'banking/loans/record-payment', 'teal', 'bi bi-currency-dollar'),
          card('Add Lender', 'bi bi-person-fill', 'banking/loans/lenders/add', 'green', 'bi bi-plus-lg'),
          card('Lender Details', 'bi bi-people-fill', 'banking/loans/lenders/details', 'indigo', 'bi bi-list-ul'),
          card('Add Org Loan', 'bi bi-building', 'banking/organization-loans/add', 'amber', 'bi bi-plus-lg'),
          card('Org Loans Details', 'bi bi-card-list', 'banking/organization-loans/details', 'violet', 'bi bi-list-ul'),
          card('Org Loan Payment', 'bi bi-cash-coin', 'banking/organization-loans/record-payment', 'orange', 'bi bi-currency-dollar'),
          card('Org Collect Loan', 'bi bi-cash-stack', 'banking/organization-loans/record-collection', 'teal', 'bi bi-cash-stack'),
          card('Add Organization', 'bi bi-building-fill', 'banking/organization-loans/organizations/add', 'sky', 'bi bi-plus-lg'),
          card('Organization Details', 'bi bi-card-list', 'banking/organization-loans/organizations/details', 'slate', 'bi bi-list-ul'),
          card('Make Deposit', 'bi bi-arrow-down-circle-fill', 'banking/make-deposit', 'green', 'bi bi-bank'),
          card('Reconciliations History', 'bi bi-clock-history', 'banking/reconciliations-history', 'indigo', 'bi bi-check2-circle'),
          card('Bank Accounts', 'bi bi-bank2', 'banking/accounts', 'blue', 'bi bi-list-ul'),
        ],
      });

      sections.push({
        key: 'company',
        title: 'Company',
        icon: 'bi bi-building',
        items: [
          card('Dashboard', 'bi bi-grid-3x3-gap-fill', '', 'blue', 'bi bi-speedometer2'),
          card('Import From File', 'bi bi-file-earmark-arrow-up-fill', 'importcsv', 'green', 'bi bi-upload'),
          card('Review Staged Imports', 'bi bi-clipboard2-check-fill', 'import-staging', 'teal', 'bi bi-list-check'),
          card('Journal Entry', 'bi bi-journal-text', 'journalentry', 'indigo', 'bi bi-pencil'),
          card('Transactions Center', 'bi bi-arrow-left-right', 'transactions', 'violet', 'bi bi-list-ul'),
          card('New Account', 'bi bi-plus-circle-fill', 'new-account', 'green', 'bi bi-wallet2'),
          card('Company Preferences', 'bi bi-gear-fill', 'company-preferences', 'slate', 'bi bi-sliders'),
          card('Budget', 'bi bi-pie-chart-fill', 'company/budget', 'amber', 'bi bi-graph-up'),
          card('Customer Group List', 'bi bi-people-fill', 'company/support-lists/customer-group', 'blue', 'bi bi-list-ul'),
          card('Vendor Group List', 'bi bi-shop', 'company/support-lists/vendor-group', 'orange', 'bi bi-list-ul'),
          card('Invoice Frequency List', 'bi bi-arrow-repeat', 'company/support-lists/invoice-frequency', 'indigo', 'bi bi-calendar3'),
          card('Timesheet Frequency List', 'bi bi-calendar-week', 'company/support-lists/timesheet-frequency', 'sky', 'bi bi-clock'),
          card('Payment Term List', 'bi bi-clock', 'company/support-lists/payment-term', 'teal', 'bi bi-list-ul'),
          card('Item Group List', 'bi bi-boxes', 'company/support-lists/item-group', 'violet', 'bi bi-grid'),
          card('Warehouses List', 'bi bi-building', 'company/support-lists/warehouses', 'slate', 'bi bi-list-ul'),
          card('Shipping Method List', 'bi bi-truck', 'company/support-lists/shipping-method', 'amber', 'bi bi-list-ul'),
          card('Customer Notes List', 'bi bi-journal-bookmark-fill', 'company/support-lists/notes-list', 'blue', 'bi bi-card-text'),
          card('Vendor Notes List', 'bi bi-journal-bookmark', 'company/support-lists/vendor-notes-list', 'orange', 'bi bi-card-text'),
          card('Note Tag List', 'bi bi-tags-fill', 'company/support-lists/note-tags', 'violet', 'bi bi-tag'),
          card('Note Type List', 'bi bi-card-list', 'company/support-lists/note-types', 'indigo', 'bi bi-list-ul'),
          card('Note Priority List', 'bi bi-flag-fill', 'company/support-lists/note-priorities', 'red', 'bi bi-exclamation'),
          card('New Fixed Asset', 'bi bi-plus-circle-fill', 'company/fixed-assets/new', 'green', 'bi bi-building'),
          card('Depreciation', 'bi bi-graph-down', 'company/fixed-assets/depreciation', 'orange', 'bi bi-graph-down-arrow'),
          card('Pending Items List', 'bi bi-hourglass-split', 'company/fixed-assets/pending-items', 'amber', 'bi bi-list-ul'),
          card('Registered Items List', 'bi bi-check-circle-fill', 'company/fixed-assets/registered-items', 'teal', 'bi bi-list-check'),
          card('Sold & Disposed Items', 'bi bi-x-circle-fill', 'company/fixed-assets/sold', 'red', 'bi bi-trash'),
          card('Merge Customers', 'bi bi-people-fill', 'company/merge-accounts/customers', 'blue', 'bi bi-link-45deg'),
          card('Merge Vendors', 'bi bi-shop', 'company/merge-accounts/vendors', 'orange', 'bi bi-link-45deg'),
          card('Merge Accounts', 'bi bi-wallet2', 'company/merge-accounts/accounts', 'indigo', 'bi bi-link-45deg'),
          card('Merge Items', 'bi bi-box-seam', 'company/merge-accounts/items', 'violet', 'bi bi-link-45deg'),
          card('Accounts List', 'bi bi-wallet-fill', 'company-account-list', 'sky', 'bi bi-list-ul'),
          card('Items', 'bi bi-box-seam', 'company/lists/items', 'amber', 'bi bi-grid'),
          card('Customers', 'bi bi-people-fill', 'customers/list', 'blue', 'bi bi-list-ul'),
          card('Vendors', 'bi bi-shop', 'vendors/list', 'orange', 'bi bi-list-ul'),
          card('Consultants', 'bi bi-person-fill', 'consultant/list', 'teal', 'bi bi-list-ul'),
          card('Sales Persons', 'bi bi-person-badge-fill', 'company/lists/salespersons', 'green', 'bi bi-list-ul'),
          card('Users Activity Log', 'bi bi-activity', 'company/lists/activitylog', 'slate', 'bi bi-clock-history'),
          card('Recurring Transactions', 'bi bi-arrow-repeat', 'company/lists/recurring', 'indigo', 'bi bi-arrow-repeat'),
          card('Reminders List', 'bi bi-bell-fill', 'company/lists/reminders', 'red', 'bi bi-list-ul'),
        ],
      });

      sections.push({
        key: 'reports',
        title: 'Reports',
        icon: 'bi bi-bar-chart',
        items: [
          card('Reports Home', 'bi bi-house-door-fill', 'reports/home', 'blue', 'bi bi-bar-chart-fill'),
          card('Profit And Loss', 'bi bi-graph-up', 'reports/company-finance/profit-loss', 'green', 'bi bi-currency-dollar'),
          card('Balance Sheet', 'bi bi-file-earmark-spreadsheet-fill', 'reports/company-finance/balance-sheet', 'indigo', 'bi bi-table'),
          card('Cash Flow Report', 'bi bi-arrow-left-right', 'reports/company-finance/cash-flow', 'teal', 'bi bi-water'),
          card('Trial Balance', 'bi bi-calculator-fill', 'reports/company-finance/trial-balance', 'violet', 'bi bi-plus-slash-minus'),
          card('Transaction Detail by Account', 'bi bi-list-ul', 'reports/company-finance/transaction-detail', 'sky', 'bi bi-journal-text'),
          card('Income by Customer Detail', 'bi bi-people-fill', 'reports/company-finance/income-by-customer', 'blue', 'bi bi-graph-up'),
          card('General Ledger Report', 'bi bi-journal-text', 'reports/company-finance/general-ledger', 'slate', 'bi bi-book'),
          card('Expense Report', 'bi bi-cash-stack', 'reports/company-finance/expense', 'orange', 'bi bi-receipt'),
          card('Automatic Transactions', 'bi bi-arrow-repeat', 'reports/company-finance/automatic-transactions', 'amber', 'bi bi-lightning-fill'),
          card('Reconciliation Reports', 'bi bi-check-circle-fill', 'reports/company-finance/reconciliation', 'teal', 'bi bi-check2'),
          card('A/R Aging Summary', 'bi bi-clock-history', 'reports/customers-receivable/ar-aging-summary', 'blue', 'bi bi-bar-chart'),
          card('A/R Aging Detail', 'bi bi-list-check', 'reports/customers-receivable/ar-aging-detail', 'indigo', 'bi bi-list-ul'),
          card('Customers Statement', 'bi bi-file-text-fill', 'reports/customers-receivable/client-statement', 'sky', 'bi bi-people'),
          card('Customer Transaction History', 'bi bi-clock-history', 'reports/customers-receivable/customer-transaction-history', 'violet', 'bi bi-clock'),
          card('Sales by Customer Summary', 'bi bi-bar-chart-fill', 'reports/sales/customer-summary', 'green', 'bi bi-people'),
          card('Sales by Customer Detail', 'bi bi-list-ul', 'reports/sales/customer-detail', 'teal', 'bi bi-list-check'),
          card('Sales by Consultant Summary', 'bi bi-bar-chart-fill', 'reports/sales/item-summary', 'amber', 'bi bi-person'),
          card('Sales by Consultant Detail', 'bi bi-list-ul', 'reports/sales/item-detail', 'orange', 'bi bi-list-check'),
          card('A/P Aging Summary', 'bi bi-clock-history', 'reports/vendors-payables/ap-aging-summary', 'red', 'bi bi-shop'),
          card('A/P Aging Detail', 'bi bi-list-check', 'reports/vendors-payables/ap-aging-detail', 'orange', 'bi bi-list-ul'),
          card('Vendors Statement', 'bi bi-file-text-fill', 'reports/vendors-payables/vendors-statement', 'slate', 'bi bi-shop'),
          card('Vendor Transaction History', 'bi bi-clock-history', 'reports/vendors-payables/vendor-transaction-history', 'indigo', 'bi bi-clock'),
          card('Purchase by Vendor Summary', 'bi bi-bar-chart-fill', 'reports/purchase/purchase-by-vendor-summary', 'blue', 'bi bi-cart'),
          card('Purchase by Vendor Detail', 'bi bi-list-ul', 'reports/purchase/purchase-by-vendor-detail', 'sky', 'bi bi-list-check'),
          card('Purchase by Item Summary', 'bi bi-bar-chart-fill', 'reports/purchase/purchase-by-item-summary', 'violet', 'bi bi-box'),
          card('Purchase by Item Detail', 'bi bi-list-ul', 'reports/purchase/purchase-by-item-detail', 'indigo', 'bi bi-list-check'),
          card('Budget Overview', 'bi bi-eye-fill', 'reports/budget/overview', 'teal', 'bi bi-pie-chart'),
          card('Budget vs Actuals', 'bi bi-graph-up-arrow', 'reports/budget/vs-actuals', 'green', 'bi bi-graph-up'),
          card('Depreciation Report', 'bi bi-graph-down', 'reports/fixed-asset/depreciation', 'orange', 'bi bi-building'),
          card('Inventory Valuation Summary', 'bi bi-bar-chart-fill', 'reports/inventory/inventory-valuation-summary', 'amber', 'bi bi-box-seam'),
          card('Inventory Valuation Details', 'bi bi-list-ul', 'reports/inventory/inventory-valuation-details', 'violet', 'bi bi-list-check'),
          card('Inventory Stock Status By Item', 'bi bi-box-fill', 'reports/inventory/inventory-stock-status-by-item', 'sky', 'bi bi-clipboard-data'),
          card('Inventory Details', 'bi bi-list-check', 'reports/inventory/inventory-details', 'indigo', 'bi bi-box'),
          card('Missing Checks', 'bi bi-exclamation-triangle-fill', 'reports/banking/missing-checks', 'red', 'bi bi-bank'),
          card('Reconciliation Discrepancy', 'bi bi-x-circle-fill', 'reports/banking/reconciliation-discrepancy', 'orange', 'bi bi-exclamation'),
          card('Deposit Detail', 'bi bi-arrow-down-circle-fill', 'reports/banking/deposit-detail', 'green', 'bi bi-bank'),
          card('Check Detail', 'bi bi-pencil-square', 'reports/banking/check-detail', 'blue', 'bi bi-list-ul'),
          card('Pay Slip Summary', 'bi bi-bar-chart-fill', 'reports/payroll/pay-slip-summary', 'teal', 'bi bi-wallet2'),
          card('Pay Slip Detail', 'bi bi-list-ul', 'reports/payroll/pay-slip-detail', 'indigo', 'bi bi-receipt'),
          card('Pay Sheet', 'bi bi-table', 'reports/payroll/pay-sheet', 'slate', 'bi bi-grid-3x3'),
          card('Pay Head Summary Report', 'bi bi-bar-chart-fill', 'reports/payroll/pay-head-summary', 'violet', 'bi bi-cash'),
          card('Pay Head Detail Report', 'bi bi-list-ul', 'reports/payroll/pay-head-detail', 'sky', 'bi bi-list-check'),
          card('Tax Summary Report', 'bi bi-receipt', 'reports/payroll/tax-summary-report', 'amber', 'bi bi-percent'),
        ],
      });

      sections.push({
        key: 'settings',
        title: 'Settings',
        icon: 'bi bi-gear',
        items: [
          card('Company Settings', 'bi bi-building', 'company-preferences', 'blue', 'bi bi-gear-fill'),
          card('Users', 'bi bi-people-fill', 'settings/users', 'indigo', 'bi bi-person-gear'),
          card('Update Profile', 'bi bi-person-circle', 'settings/update-profile', 'green', 'bi bi-pencil'),
          card('Change Password', 'bi bi-key-fill', 'settings/change-password', 'amber', 'bi bi-shield-lock'),
          card('Session Alert', 'bi bi-alarm-fill', 'settings/session-alert', 'orange', 'bi bi-bell'),
          card('Email Setup', 'bi bi-envelope-fill', 'settings/email-setup', 'sky', 'bi bi-gear'),
          card('Email Signature', 'bi bi-pen-fill', 'settings/email-signature', 'violet', 'bi bi-vector-pen'),
          card('Translation', 'bi bi-translate', 'settings/translation', 'teal', 'bi bi-globe'),
          card('UI Preferences', 'bi bi-palette-fill', 'settings/ui-preferences', 'indigo', 'bi bi-sliders'),
          card('Document Branding', 'bi bi-file-earmark-image-fill', 'settings/document-branding', 'blue', 'bi bi-image'),
          card('Check Print Setting', 'bi bi-printer-fill', 'settings/check-print', 'slate', 'bi bi-printer'),
          card('Integration Keys', 'bi bi-key', 'settings/integration-keys', 'amber', 'bi bi-plugin'),
          card('SMS Notifications', 'bi bi-chat-text-fill', 'settings/messaging-notifications', 'green', 'bi bi-phone'),
          card('Email Notifications', 'bi bi-envelope-fill', 'settings/email-notifications', 'sky', 'bi bi-bell'),
          card('Dunning Letter', 'bi bi-envelope-exclamation-fill', 'settings/dunning-letter-categories', 'red', 'bi bi-exclamation'),
          card('Journal Entry Guide', 'bi bi-journal-text', 'settings/journal-entry-guide', 'indigo', 'bi bi-book'),
          card('Account Configuration', 'bi bi-gear-fill', 'settings/account-configuration', 'violet', 'bi bi-wallet2'),
        ],
      });

      return sections;
    },
  },
  mounted() {
    this.loadUsage();
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onGlobalSlash);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onGlobalSlash);
  },
  watch: {
    companyId() {
      this.loadUsage();
    },
  },
  methods: {
    toPath(relativePath) {
      const base = `/company/${this.companyId}/dashboard`;
      return relativePath ? `${base}/${relativePath}` : base;
    },
    loadUsage() {
      try {
        const raw = localStorage.getItem(this.usageStorageKey);
        this.usageMap = raw ? JSON.parse(raw) : {};
      } catch (_) {
        this.usageMap = {};
      }
    },
    saveUsage() {
      try {
        localStorage.setItem(this.usageStorageKey, JSON.stringify(this.usageMap));
      } catch (_) {
        // ignore quota / private mode errors
      }
    },
    recordUsage(item) {
      if (!item || item.path === undefined || item.path === null) return;
      const path = item.path;
      const existing = this.usageMap[path] || { path, count: 0 };
      this.usageMap = {
        ...this.usageMap,
        [path]: {
          path,
          label: item.label || existing.label || '',
          icon: item.icon || existing.icon || '',
          tone: item.tone || existing.tone || 'slate',
          badge: item.badge || existing.badge || '',
          section: item.section || existing.section || '',
          count: (existing.count || 0) + 1,
          lastUsed: Date.now(),
        },
      };
      this.saveUsage();
    },
    clearRecentUsage() {
      this.usageMap = {};
      try {
        localStorage.removeItem(this.usageStorageKey);
      } catch (_) {
        // ignore
      }
    },
    onSearchFocus() {
      this.searchFocused = true;
    },
    onSearchInput() {
      this.activeSuggestionIndex = 0;
    },
    onDocumentClick(event) {
      const root = this.$refs.searchRoot;
      if (root && !root.contains(event.target)) {
        this.searchFocused = false;
      }
    },
    onGlobalSlash(event) {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = (event.target && event.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      event.preventDefault();
      this.$refs.searchInput?.focus();
    },
    onSearchKeydown(event) {
      if (!this.showSuggestions && !['ArrowDown', 'Enter'].includes(event.key)) {
        if (event.key === 'Escape') {
          this.clearSearch();
          this.$refs.searchInput?.blur();
        }
        return;
      }

      const count = this.searchSuggestions.length;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!count) return;
        this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % count;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!count) return;
        this.activeSuggestionIndex = (this.activeSuggestionIndex - 1 + count) % count;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const item = this.searchSuggestions[this.activeSuggestionIndex];
        if (item) this.selectSuggestion(item);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.searchFocused = false;
        this.$refs.searchInput?.blur();
      }
    },
    selectSuggestion(item) {
      if (!item) return;
      this.recordUsage(item);
      this.searchQuery = '';
      this.searchFocused = false;
      this.activeSuggestionIndex = 0;
      const path = this.toPath(item.path);
      if (item.path === 'consultant/payroll/record') {
        this.$router.push({ path, state: { recordPayrollFresh: true } });
        return;
      }
      this.$router.push(path);
    },
    clearSearch() {
      this.searchQuery = '';
      this.activeSuggestionIndex = 0;
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    highlightMatch(label) {
      const q = this.searchQuery.trim();
      if (!q) return this.escapeHtml(label);
      const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQ})`, 'ig');
      return this.escapeHtml(label).replace(regex, '<mark>$1</mark>');
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
  },
};
</script>
